import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, excelDate, seededRandom } from "../spec";

const SHEET = "TX Inventario";
const TABLE = "Inventario";

const ALMACENES = ["MTY", "GDL", "CDM", "PUE"] as const;
const CATEGORIAS = ["ELE", "MUE", "PAP", "LIM", "COM"] as const;
const PRODUCTOS = [
  "Monitor 24 pulgadas", "Silla de visita", "Papel bond carta", "Desinfectante 1 L", "Teclado inalámbrico",
  "Escritorio en L", "Folders oficio", "Toallas de papel", "Mouse vertical", "Archivero 3 cajones",
] as const;
const LETRAS = "ABCDEFGHJKLMNPRSTUVWXYZ";

const rnd = seededRandom(7071);
const pad = (n: number, size: number) => String(n).padStart(size, "0");

// Código, Producto, RFC proveedor
const ROWS = Array.from({ length: 30 }, (_, i) => {
  const codigo = `${rnd.pick(ALMACENES)}-${rnd.int(24, 26)}-${rnd.pick(CATEGORIAS)}-${pad(rnd.int(1, 9999), 5)}`; // always has leading zeros
  const year = rnd.int(0, 24);
  const month = rnd.int(1, 12);
  const day = rnd.int(1, 28);
  const letras = Array.from({ length: 3 }, () => LETRAS[rnd.int(0, LETRAS.length - 1)]).join("");
  const homoclave = `${LETRAS[rnd.int(0, LETRAS.length - 1)]}${rnd.int(1, 9)}${LETRAS[rnd.int(0, LETRAS.length - 1)]}`;
  let rfc = `${letras}${pad(year, 2)}${pad(month, 2)}${pad(day, 2)}${homoclave}`;
  // A few RFC were typed wrong: one character short or one extra.
  if (i % 9 === 4) rfc = rfc.slice(0, 11);
  if (i % 11 === 7) rfc = `${rfc}X`;
  return [codigo, rnd.pick(PRODUCTOS), rfc] as [string, string, string];
});

type Row = (typeof ROWS)[number];
const fechaRfc = (rfc: string) =>
  excelDate(`20${rfc.slice(3, 5)}-${rfc.slice(5, 7)}-${rfc.slice(7, 9)}`);

export const spec: FuncionProjectSpec = {
  projectId: "funciones-extraer",
  sheets: [
    {
      name: SHEET,
      title: "Códigos de inventario",
      subtitle: "Cada código guarda almacén, año, categoría y folio: sepáralos con fórmulas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Código", "Producto", "RFC proveedor"],
          rows: ROWS,
          textColumns: ["Código", "RFC proveedor"],
          answerColumns: [
            "Almacén",
            "Folio",
            "Categoría",
            "Folio número",
            "RFC válido",
            "Constitución",
            "Código corto",
          ],
          formats: { Constitución: "dd/mm/yyyy" },
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Almacén", fns: ["LEFT"], exact: true,
      expected: ROWS.map((r) => r[0].slice(0, 3)),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Folio", fns: ["RIGHT"], exact: true,
      expected: ROWS.map((r) => r[0].slice(-5)),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Categoría", fns: ["MID"], exact: true,
      expected: ROWS.map((r) => r[0].slice(7, 10)),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Folio número", fns: ["VALUE", "RIGHT"],
      expected: ROWS.map((r) => Number(r[0].slice(-5))),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "RFC válido", fns: ["IF", "LEN"], exact: true,
      expected: ROWS.map((r: Row) => (r[2].length === 12 ? "OK" : "Revisar")),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Constitución", fns: ["DATE", "MID"],
      minCounts: { MID: 3 },
      expected: ROWS.map((r) => fechaRfc(r[2])),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Código corto", fns: ["LEFT", "RIGHT"], exact: true,
      expected: ROWS.map((r) => `${r[0].slice(0, 3)}-${r[0].slice(-5)}`),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-extraer",
  title: "IZQUIERDA, DERECHA y EXTRAE: Códigos de inventario",
  subtitle: "Sacar pedazos de un texto por posición",
  description:
    "El sistema de inventario genera códigos como MTY-26-ELE-00457: almacén, año, categoría y folio, todo en una sola celda. Además, el RFC de cada proveedor trae escondida su fecha de constitución.\n\nIZQUIERDA toma caracteres del inicio, DERECHA del final y EXTRAE de en medio. Funcionan perfecto cuando cada parte ocupa siempre la misma posición.",
  topics: [
    "IZQUIERDA, DERECHA y EXTRAE",
    "Texto que parece número: VALOR",
    "Validar longitud con LARGO",
    "Armar una fecha con FECHA y tres EXTRAE",
    "Unir pedazos con &",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Almacén",
      description:
        "En la hoja <strong>TX Inventario</strong>, columna <strong>Almacén</strong>: obtén las <strong>3 primeras letras</strong> del Código (MTY, GDL…).",
      solution: [`Selecciona ${F("D5")}.`, `Escribe ${F("=IZQUIERDA([@Código],3)")}.`],
      expectedResult: "MTY, GDL, CDM o PUE",
      finalFormula: "=IZQUIERDA([@Código],3)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Folio",
      description: "En la columna <strong>Folio</strong>: obtén los <strong>5 últimos caracteres</strong> del Código.",
      solution: [`Selecciona ${F("E5")}.`, `Escribe ${F("=DERECHA([@Código],5)")}.`],
      expectedResult: "00457 (como texto, con sus ceros)",
      finalFormula: "=DERECHA([@Código],5)",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Categoría",
      description:
        "En la columna <strong>Categoría</strong>: obtén las 3 letras de la categoría, que empiezan en el <strong>carácter 8</strong> del Código (MTY-26-<strong>ELE</strong>-00457).",
      solution: [
        `Selecciona ${F("F5")}.`,
        "EXTRAE(texto, posición inicial, número de caracteres). Cuenta los guiones: M-T-Y-guion-2-6-guion son 7 caracteres, así que la categoría empieza en el 8.",
        `Escribe ${F("=EXTRAE([@Código],8,3)")}.`,
      ],
      expectedResult: "ELE, MUE, PAP, LIM o COM",
      finalFormula: "=EXTRAE([@Código],8,3)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Folio como número",
      description:
        "En la columna <strong>Folio número</strong>: el folio de la Tarea 2 es <strong>texto</strong> (\"00457\") y no se puede sumar ni ordenar como número. Conviértelo en el número <strong>457</strong>.",
      solution: [
        `Selecciona ${F("G5")}.`,
        "Las funciones de texto siempre devuelven texto. VALOR lo convierte en número y quita los ceros de la izquierda.",
        `Escribe ${F("=VALOR(DERECHA([@Código],5))")}.`,
      ],
      expectedResult: "457 (número)",
      finalFormula: "=VALOR(DERECHA([@Código],5))",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: ¿RFC bien capturado?",
      description: conditions("En la columna <strong>RFC válido</strong>: el RFC de una empresa tiene exactamente <strong>12 caracteres</strong>.", [
        "Si tiene 12 → <strong>OK</strong>",
        "Si tiene más o menos → <strong>Revisar</strong>",
      ]),
      solution: [
        `Selecciona ${F("H5")}.`,
        "LARGO cuenta los caracteres de un texto.",
        `Escribe ${F('=SI(LARGO([@[RFC proveedor]])=12,"OK","Revisar")')}.`,
      ],
      expectedResult: "OK / Revisar",
      finalFormula: '=SI(LARGO([@[RFC proveedor]])=12,"OK","Revisar")',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Fecha de constitución",
      description: conditions(
        "En la columna <strong>Constitución</strong>: en el RFC, los caracteres <strong>4 a 9</strong> son la fecha en formato AAMMDD (todas son del año 2000 en adelante):",
        [
          "Año → caracteres 4 y 5 (súmales 2000)",
          "Mes → caracteres 6 y 7",
          "Día → caracteres 8 y 9",
        ],
        "Arma una fecha real con <strong>FECHA</strong> y tres <strong>EXTRAE</strong>."
      ),
      solution: [
        `Selecciona ${F("I5")}.`,
        "FECHA(año, mes, día) acepta texto numérico como \"03\", así que no hace falta VALOR.",
        `Escribe ${F("=FECHA(2000+EXTRAE([@[RFC proveedor]],4,2),EXTRAE([@[RFC proveedor]],6,2),EXTRAE([@[RFC proveedor]],8,2))")}.`,
      ],
      expectedResult: "Fecha de constitución de cada proveedor",
      finalFormula:
        "=FECHA(2000+EXTRAE([@[RFC proveedor]],4,2),EXTRAE([@[RFC proveedor]],6,2),EXTRAE([@[RFC proveedor]],8,2))",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Código corto",
      description:
        "En la columna <strong>Código corto</strong>: arma un código abreviado con el <strong>almacén</strong>, un guion y el <strong>folio</strong>: <strong>MTY-00457</strong>. Hazlo directo desde el Código, sin usar las columnas anteriores.",
      solution: [
        `Selecciona ${F("J5")}.`,
        `${F("&")} une textos.`,
        `Escribe ${F('=IZQUIERDA([@Código],3)&"-"&DERECHA([@Código],5)')}.`,
      ],
      expectedResult: "MTY-00457",
      finalFormula: '=IZQUIERDA([@Código],3)&"-"&DERECHA([@Código],5)',
    },
  ],
};
